import { InputModal } from "../../modals/InputModal";
import { css } from "@emotion/css";
import { useRef } from "react";
import { useLabel } from "react-aria";
import { ComboBoxProps as AriaComboBoxProps } from "react-aria-components";
import { useTranslation } from "react-i18next";

import { _ComboBox } from "./assets/_ComboBox";
import { _ListBox } from "./assets/_ListBox";
import { useRefOffsetWidthResize } from "../../../hooks/useRefOffsetWidthResize";
import { _Popover } from "../../_assets/_Popover";
import { _Text } from "../../_assets/_Text";
import { GhostIconButton } from "../../button/GhostIconButton";
import { Divider } from "../../divider/Divider";
import { _Input } from "../_assets/_Input";
import { _InputWrapper } from "../_assets/_InputWrapper";
import {
  FieldErrorsProps,
  FieldErrors,
} from "../_assets/input-wrapper/FieldErrors";
import { FormLabelRAC } from "../_assets/input-wrapper/FormLabelRAC";

export interface SearchableSelectProps<T extends object>
  extends Omit<AriaComboBoxProps<T>, "children"> {
  label?: string;
  description?: string;
  placeholder?: string;
  errors?: FieldErrorsProps["children"];
  emptyMessage?: string;
  allowSearchWhenEmpty?: boolean;
  isLoading?: boolean;
  /**
   * When false, keeps the text input mounted while loading (avoids focus loss).
   * Loading still shows on the popover.
   */
  showLoadingInInput?: boolean;
  info?: React.ReactNode;
  options?: {
    more_info?: {
      linkText: string;
      modalHeading: string;
      modalBody: string;
    };
  };
  children: React.ReactNode | ((item: T) => React.ReactNode);
  footer?: React.ReactNode;
  testID: string;
}

export function SearchableSelect<T extends object>(
  props: SearchableSelectProps<T>
) {
  const { t } = useTranslation();
  const { labelProps, fieldProps } = useLabel(props);
  const ref = useRef<HTMLDivElement>(null);
  const { inputWidth } = useRefOffsetWidthResize(ref);

  const {
    label,
    description,
    placeholder = t("Please select"),
    errors,
    emptyMessage,
    allowSearchWhenEmpty,
    isLoading,
    showLoadingInInput = true,
    items,
    children,
    footer,
    testID,
    ...comboBoxProps
  } = props;

  const isEmpty =
    (!isLoading && !comboBoxProps.allowsCustomValue && !items && !children) ||
    (!isLoading &&
      !comboBoxProps.allowsCustomValue &&
      items &&
      items &&
      [...items].length < 1);

  const isDisabling = isEmpty
    ? true
    : comboBoxProps.isDisabled || props.isDisabled;

  return (
    <div className={styles.wrapper} data-testId={testID}>
      <div className={styles.header_wrapper}>
        {props.label && (
          <div className={styles.label_wrapper}>
            <FormLabelRAC
              isRequired={props.isRequired}
              info={props.info}
              {...labelProps}
            >
              {props.label}
            </FormLabelRAC>
          </div>
        )}
        <InputModal {...props.options?.more_info} />
      </div>

      <_ComboBox
        {...comboBoxProps}
        {...fieldProps}
        isDisabled={isDisabling}
        data-testid={`${testID}-combobox`}
      >
        <div ref={ref}>
          <_InputWrapper isEmpty={isEmpty} emptyMessage={emptyMessage}>
            <_Input
              isLoading={showLoadingInInput && isLoading}
              placeholder={placeholder}
              data-testid={`${testID}-input`}
            />

            {!isDisabling && (
              <GhostIconButton
                icon={comboBoxProps.selectedKey ? "expand_more" : "search"}
                isDisabled={isEmpty}
              />
            )}
          </_InputWrapper>
        </div>
        {description && (
          <_Text style="input/helper" color="text-weak" slot="description">
            {description}
          </_Text>
        )}
        <FieldErrors>{errors}</FieldErrors>
        <_Popover
          offset={8}
          isLoading={isLoading}
          className={css({ width: `${inputWidth}px` })}
        >
          <_ListBox selectionMode="multiple" items={items}>
            {children}
          </_ListBox>
          {footer && <Divider />}
          {footer}
        </_Popover>
      </_ComboBox>
    </div>
  );
}

const styles = {
  wrapper: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-1)",
  }),
  header_wrapper: css({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  }),
  label_wrapper: css({
    flex: 1,
  }),
};
